import { useEffect, useState } from "react";
import { DatePicker } from "antd";
import Swal from "sweetalert2";
import { GetPacientesAll } from "../../Service/Service.paciente";
import { ListarEstadosResultados } from "../../Service/Service.util";
import { ControllerListarSegurosClinica } from "../../Service/Services.seguros";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import SelectSearchable from "../SelectSeachble/SelectSearchble";
import Boton from "../UI/Boton";
import { ChipFiltro } from "../UI/Chip";
import { IconoBuscar } from "../UI/Iconos";

// RESULTADOS-04-FILTROS-PERFORMANCE-01: panel de filtros SIEMPRE visible
// encima de la tabla (reemplaza al Offcanvas <FiltrosFacturas> que habia que
// abrir con un boton). Los filtros son los mismos que ya procesaba el backend
// (fecha + paciente) mas los nuevos server-side pedidos por el ticket
// (busqueda general, procedimiento, estado del resultado). No dispara consultas
// mientras se escribe: solo al pulsar "Buscar".
const ESTADO_INICIAL = {
    busqueda: "",
    rangoFechas: null,
    paciente: null,
    procedimiento: "",
    estado_id: null,
    seguro: null, // "particular" | <id seguro> | null
};

// RESULTADOS-07: opción fija "Particular" (facturas.seguro_id NULL) al inicio
// del catálogo de coberturas.
const OPCION_PARTICULAR = { value: "particular", label: "Particular" };

const PanelFiltrosResultados = ({ onBuscar, onLimpiar }) => {
    const [draft, setDraft] = useState(ESTADO_INICIAL);

    const [pacientes, setPacientes] = useState([]);
    const [pacienteOption, setPacienteOption] = useState(null);

    const [estados, setEstados] = useState([]);
    const [estadoOption, setEstadoOption] = useState(null);

    const [seguros, setSeguros] = useState([OPCION_PARTICULAR]);
    const [seguroOption, setSeguroOption] = useState(null);

    const loadPacientes = async (search = null) => {
        try {
            const result = await GetPacientesAll(1, 10, search);
            if (result && result.success) {
                setPacientes(
                    result.data.map((d) => ({
                        value: d.idpaciente,
                        label: `${d.cedula} - ${d.nombres_completos}`,
                    }))
                );
            }
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: error.message || "No se pudieron cargar pacientes" });
        }
    };

    const loadEstados = async () => {
        try {
            const result = await ListarEstadosResultados();
            if (result && result.success) {
                setEstados(result.data.map((e) => ({ value: e.id, label: e.nombre })));
            }
        } catch (error) {
            // El filtro de estado es opcional -- si falla la carga, el panel
            // sigue siendo utilizable con el resto de filtros.
        }
    };

    const loadSeguros = async () => {
        try {
            const idclinica = GetTokenDecoded()?.idclinica;
            const data = idclinica ? await ControllerListarSegurosClinica(idclinica) : null;
            const opts = Array.isArray(data)
                ? data
                    // RESULTADOS-07-QA-FIX: la opción sintética "Particular"
                    // (value "particular") ya cubre NULL + seguros de tipo
                    // PARTICULAR -> se ocultan del combo para no duplicar.
                    .filter((s) => String(s?.tipoSeguro?.tipo ?? "").trim().toUpperCase() !== "PARTICULAR")
                    .map((s) => ({ value: s.idseguro, label: (s.nombre || "").trim() || `Seguro ${s.idseguro}` }))
                : [];
            setSeguros([OPCION_PARTICULAR, ...opts]);
        } catch (error) {
            // el filtro de cobertura es opcional -- si falla, queda solo "Particular"
        }
    };

    useEffect(() => {
        loadPacientes();
        loadEstados();
        loadSeguros();
    }, []);

    // Recibe el borrador a construir: quitar un chip aplica el filtro nuevo en
    // el mismo gesto, sin esperar a que el usuario pulse Buscar otra vez.
    const construirFiltros = (origen = draft) => {
        const { rangoFechas } = origen;
        return {
            busqueda: origen.busqueda?.trim() || null,
            procedimiento: origen.procedimiento?.trim() || null,
            paciente: origen.paciente || null,
            estado_id: origen.estado_id || null,
            seguro: origen.seguro ?? null,
            fecha_inicial: rangoFechas?.[0]?.format("YYYY-MM-DD") || null,
            fecha_final: rangoFechas?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss") || null,
        };
    };

    // SISTEMA-DE-DISENO: lo aplicado siempre visible como chips removibles. Es
    // una foto de lo que se envió al servidor, no del borrador: si escribes en
    // un campo y no pulsas Buscar, el chip no aparece -- porque ese filtro no
    // está aplicado.
    const [aplicados, setAplicados] = useState([]);

    const construirChips = (origen, opciones) => {
        const chips = [];
        const [d0, d1] = origen.rangoFechas || [];
        if (origen.busqueda?.trim()) chips.push({ campo: "busqueda", texto: `Búsqueda: ${origen.busqueda.trim()}` });
        if (d0 && d1) chips.push({ campo: "rangoFechas", texto: `${d0.format("DD/MM/YYYY")} — ${d1.format("DD/MM/YYYY")}` });
        if (origen.paciente) chips.push({ campo: "paciente", texto: `Paciente: ${opciones.paciente?.label ?? origen.paciente}` });
        if (origen.procedimiento?.trim()) chips.push({ campo: "procedimiento", texto: `Procedimiento: ${origen.procedimiento.trim()}` });
        if (origen.estado_id) chips.push({ campo: "estado_id", texto: `Estado: ${opciones.estado?.label ?? origen.estado_id}` });
        if (origen.seguro) chips.push({ campo: "seguro", texto: `Cobertura: ${opciones.seguro?.label ?? origen.seguro}` });
        return chips;
    };

    const handleBuscar = () => {
        onBuscar(construirFiltros());
        setAplicados(construirChips(draft, { paciente: pacienteOption, estado: estadoOption, seguro: seguroOption }));
    };

    const handleLimpiar = () => {
        setDraft(ESTADO_INICIAL);
        setPacienteOption(null);
        setEstadoOption(null);
        setSeguroOption(null);
        setAplicados([]);
        onLimpiar();
    };

    const quitarChip = (campo) => {
        const nuevo = { ...draft, [campo]: ESTADO_INICIAL[campo] };
        setDraft(nuevo);
        if (campo === "paciente") setPacienteOption(null);
        if (campo === "estado_id") setEstadoOption(null);
        if (campo === "seguro") setSeguroOption(null);
        onBuscar(construirFiltros(nuevo));
        setAplicados((cs) => cs.filter((c) => c.campo !== campo));
    };

    return (
        <div className="card mb-3">
            <div className="card-body py-3">
                <div className="row g-3">
                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Búsqueda general</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Paciente o Nº documento"
                            value={draft.busqueda}
                            onChange={(e) => setDraft({ ...draft, busqueda: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && handleBuscar()}
                        />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Fecha (desde / hasta)</label>
                        <DatePicker.RangePicker
                            className="w-100"
                            value={draft.rangoFechas}
                            onChange={(dates) => setDraft({ ...draft, rangoFechas: dates })}
                            getPopupContainer={() => document.body}
                        />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Paciente</label>
                        <SelectSearchable
                            listCompany={pacientes}
                            handleChange={(opt) => {
                                setPacienteOption(opt);
                                setDraft({ ...draft, paciente: opt?.value ?? null });
                            }}
                            placeholder="Buscar paciente"
                            selectedOption={pacienteOption}
                            onInputChange={(value) => loadPacientes(value)}
                            isClearable={true}
                            // RESULTADOS-19: la busqueda de pacientes es server-side
                            // (loadPacientes). Sin esto, el filtro local por
                            // substring del label ocultaba pacientes ya devueltos
                            // por el servidor -> el filtro Paciente "no funcionaba".
                            serverSideSearch={true}
                        />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Procedimiento</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre del procedimiento"
                            value={draft.procedimiento}
                            onChange={(e) => setDraft({ ...draft, procedimiento: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && handleBuscar()}
                        />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Estado</label>
                        <SelectSearchable
                            listCompany={estados}
                            handleChange={(opt) => {
                                setEstadoOption(opt);
                                setDraft({ ...draft, estado_id: opt?.value ?? null });
                            }}
                            placeholder="Todos"
                            selectedOption={estadoOption}
                            isClearable={true}
                        />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="kc__etiqueta">Cobertura</label>
                        <SelectSearchable
                            listCompany={seguros}
                            handleChange={(opt) => {
                                setSeguroOption(opt);
                                setDraft({ ...draft, seguro: opt?.value ?? null });
                            }}
                            placeholder="Todas"
                            selectedOption={seguroOption}
                            isClearable={true}
                        />
                    </div>
                </div>

                {aplicados.length > 0 && (
                    <div className="ac-filtros-chips mt-3" data-testid="resultados-chips">
                        {aplicados.map((chip) => (
                            <ChipFiltro key={chip.campo} onQuitar={() => quitarChip(chip.campo)}>
                                {chip.texto}
                            </ChipFiltro>
                        ))}
                    </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Boton onClick={handleLimpiar} data-testid="resultados-limpiar">
                        Limpiar
                    </Boton>
                    <Boton variante="primario" icono={IconoBuscar} onClick={handleBuscar} data-testid="resultados-buscar">
                        Buscar
                    </Boton>
                </div>
            </div>
        </div>
    );
};

export default PanelFiltrosResultados;
